<template>
  <section class="flex-grow">
    <EventDetailsAlert
      :alerts="alerts"
    />
    <header class="event-details">
      <div class="aspect-square h-auto sm:w-64 sm:h-64 w-full">
        <img
          :src="coverImageUrl"
          :alt="`${event.name} Logo`"
          class="object-cover h-full w-full"
        >
      </div>
      <div class="bg-vp-base-900 flex-grow p-2 sm:p-4">
        <h3
          v-if="event.promoter"
          class="text-xs sm:text-sm text-vp-base-400 italic"
        >
          {{ event.promoter }}
        </h3>
        <h2 class="text-2xl sm:text-3xl font-bold text-vp-base-100">
          {{ event.name }}
        </h2>
        <h3
          v-if="event.support"
          class="text-xs sm:text-sm text-vp-base-100 not-italic"
        >
          {{ event.support }}
        </h3>
        <div class="flex-col sm:flex-row gap-1 sm:gap-2 flex justify-between mt-1 sm:mt-3 md:items-center">
          <div class="text-xs sm:text-sm text-vp-base-400">
            <time>{{ eventDate }}</time>
            <template v-if="isMultiDay">
              -
              <time>{{ eventEndDate }}</time>
            </template>
            <template v-else>
              <br>
              <time>{{
                $gettext('%{time} Doors', { time: doorTime }) + ', ' + $gettext('%{time} Show', { time: startTime })
              }}</time>
            </template>
          </div>
          <address class="text-xs sm:text-sm text-vp-base-400 not-italic">
            <h3 class="text-vp-base-100">
              {{ event.venue?.name }}
            </h3>
            <h3>{{ venueAddress }}</h3>
          </address>
        </div>
        <MinimumAgeDisplay
          :minimum-age="minimumAge"
        />
      </div>
    </header>
    <div class="rounded-b-md overflow-hidden border-solid border-x border-b">
      <div
        v-if="announceDescription || event.artists.length"
        class="bg-vp-blue-300 p-2 sm:p-4"
      >
        <div
          v-if="announceDescription"
          class="event-description-grid"
        >
          <div class="my-2 hidden md:block w-32 text-sm text-left font-semibold flex-shrink-0">
            {{ $gettext('About') }}
          </div>
          <div
            class="text-xs font-normal text-vp-base-600 grow overflow-hidden md:inline-flex md:flex-row-reverse items-start"
          >
            <button
              v-if="canTruncateDescription || event.artists?.length"
              type="button"
              class="md:hidden px-4 -mx-4 float-right"
              @click="toggleAbout"
            >
              <iconify-icon
                icon="mdi:chevron-up"
                class="text-4xl transition-transform"
                :class="{ '-rotate-180': aboutTruncated }"
              />
            </button>
            <div
              v-if="!descriptionEmpty"
              class="vp-event-description overflow-hidden inline grow"
              v-html="description"
            />
            <h4
              v-else
              class="lineup-label block md:hidden"
            >
              {{ $gettext("Lineup") }}
            </h4>
          </div>

          <button
            v-if="canTruncateDescription || event.artists?.length"
            type="button"
            class="hidden md:block px-4 float-right self-baseline"
            @click="toggleAbout"
          >
            <iconify-icon
              icon="mdi:chevron-up"
              class="text-4xl transition-transform"
              :class="{ '-rotate-180': aboutTruncated }"
            />
          </button>
          <template
            v-if="!aboutTruncated"
          >
            <h4 class="lineup-label hidden md:block">
              {{ $gettext("Lineup") }}
            </h4>
            <CheckoutArtists
              :artists="event.artists"
            />
            <div />
          </template>
        </div>
      </div>
      <div v-if="!props.hideTickets">
        <VPLoadingSpinner
          :loading="!ticketsLoaded"
          :top-message="$gettext('Loading tickets...')"
        >
          <div v-if="event?.usesSeatingChart">
            <SeatsioPicker />
          </div>
          <div class="-mb-1 border-vp-base-300 overflow-hidden tickets-grid">
            <template v-if="!event.usesSeatingChart">
              <TicketRow
                v-for="ticket in tickets"
                :key="ticket.id"
                :ticket="ticket"
                :read-only="readOnly"
                :includes-fees="includesFees"
              />
            </template>

            <template v-if="addons.length">
              <div class="col-span-full p-4 mt-4 border-b border-b-vp-base-300">
                <h3 class="font-bold text-xl">
                  {{ $gettext('Add-Ons') }}
                </h3>
                <h4
                  v-show="!hasSelectedTickets"
                  class="text-vp-base-600"
                >
                  {{ $gettext('Select at least one ticket to be able to select add-ons!') }}
                </h4>
              </div>

              <TicketRow
                v-for="addon in addons"
                :key="addon.id"
                :inactive="!hasSelectedTickets"
                :ticket="addon"
                :read-only="addonsReadonly"
                :includes-fees="includesFees"
              >
                <template #icon>
                  <iconify-icon
                    icon="mdi:star-box"
                    class="text-2xl"
                  />
                </template>
              </TicketRow>
            </template>
          </div>
        </VPLoadingSpinner>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, toRef } from 'vue';
import TicketRow from '@/checkout/TicketRow.vue';
import { DateTime } from 'luxon';
import { VPLoadingSpinner } from '@vp/vue-components';
import { useCheckoutStore } from '@/stores/checkout';
import { LoadingState } from '@/util/LoadingState';
import { clientOnly } from 'vike-vue/clientOnly';
import type { Checkout } from '@vp/common';
import { Util } from '@vp/common';
import sanitizeHtml from 'sanitize-html';
import { useEventImage } from '@/composables/useEventImage';
import { useTruncatedHtml } from '@/composables/useTruncatedHtml';
import EventDetailsAlert from '@/checkout/EventDetailsAlert.vue';
import { ShoppingCartItemTypeEnum } from '@/generated/graphql';
import MinimumAgeDisplay from '@/checkout/MinimumAgeDisplay.vue';
import * as Sentry from '@sentry/vue';
import CheckoutArtists from '@/checkout/CheckoutArtists.vue';

const SeatsioPicker = clientOnly(() => import('@/checkout/SeatsioPicker.vue'));

type Props = {
  readOnly?: boolean;
  event: Checkout.CheckoutEvent.CheckoutEvent;
  hideTickets?: boolean;
};

const props = defineProps<Props>();
const event = toRef(props, 'event');
const announceDescription = computed(() => event.value.description);

const checkoutStore = useCheckoutStore();

const eventDate = computed(() =>
  Util.formatTime(event.value.startTime, {
    timezone: event.value.venue?.timeZone,
    formatOptions: DateTime.DATE_MED_WITH_WEEKDAY,
  }),
);

const isMultiDay = computed(() => {
  try {
    if (!event.value.endTime) {
      return false;
    }

    const startTime = DateTime.fromISO(event.value.startTime);
    const endTime = DateTime.fromISO(event.value.endTime);

    const { days } = endTime.diff(startTime, 'days');

    if (days > 1) {
      return true;
    }

    return false;
  } catch (e) {
    Sentry.captureException(e);
    return false;
  }
});

const eventEndDate = computed(() => {
  try {
    if (!isMultiDay.value || !event.value.endTime) {
      return null;
    }

    return Util.formatTime(event.value.endTime, {
      timezone: event.value.venue?.timeZone,
      formatOptions: DateTime.DATE_MED_WITH_WEEKDAY,
    });
  } catch (e) {
    Sentry.captureException(e);
    return null;
  }
});

const doorTime = computed(() =>
  Util.formatTime(event.value.doorTime, { timezone: event.value.venue?.timeZone, formatOptions: DateTime.TIME_SIMPLE }),
);

const startTime = computed(() =>
  Util.formatTime(event.value.startTime, {
    timezone: event.value.venue?.timeZone,
    formatOptions: DateTime.TIME_SIMPLE,
  }),
);

const minimumAge = computed(() => event.value.minimumAge);

const ticketsLoaded = computed(() => checkoutStore.ticketsLoading == LoadingState.DONE);
const tickets = computed<Checkout.TicketBlueprint.CheckoutTicketBlueprint[]>(() =>
  checkoutStore.selectedEventTickets.filter(
    ({ ticketType }: Checkout.TicketBlueprint.CheckoutTicketBlueprint) => ticketType == 'ticket',
  ),
);
const addons = computed(() =>
  checkoutStore.selectedEventTickets.filter(
    ({ ticketType }: Checkout.TicketBlueprint.CheckoutTicketBlueprint) => ticketType == 'addon',
  ),
);
const coverImageUrl = useEventImage(event);
const hasSelectedTickets = computed(() =>
  checkoutStore.getCartedTickets.some(
    ({ itemType }: Checkout.ShoppingCartItem.ShoppingCartItem) =>
      (itemType == ShoppingCartItemTypeEnum.Ticket || itemType == ShoppingCartItemTypeEnum.SeatTicket) && true, // tickets.value.some(({ id }) => id == itemId),
  ),
);

const addonsReadonly = computed(() => props.readOnly);

const includesFees = computed(() =>
  [event.value.config?.includeConvenienceFee, event.value.config?.includeTaxFee, event.value.config?.includeCcFee].some(
    v => v,
  ),
);

const alerts = computed(() => checkoutStore.getEventCheckoutConfig?.alerts);

const venueAddress = computed(() => {
  const venue = props.event?.venue;
  if (venue) {
    return `${venue.street1}, ${venue.city}, ${venue.state}`;
  } else {
    return '';
  }
});

const sanitizer = (text: string, truncate: boolean) => {
  let sanitizeOptions = {};
  if (!truncate) {
    sanitizeOptions = {
      allowedTags: sanitizeHtml.defaults.allowedTags.concat(['iframe-embed']),
      allowedAttributes: {
        ...sanitizeHtml.defaults.allowedAttributes,
        'iframe-embed': ['*'],
      },
    };
  }

  return sanitizeHtml(text, sanitizeOptions);
};

const {
  text: description,
  truncate: aboutTruncated,
  canTruncate: canTruncateDescription,
} = useTruncatedHtml(announceDescription, {
  limit: 150,
  startTruncated: false,
  sanitizer,
});

onMounted(() => {
  // In order for SSR to render the full description
  aboutTruncated.value = true;
});

const descriptionEmpty = computed(() => !description || description.value.replace(/(<[^>]+>)/g, '').trim() === '');

const toggleAbout = () => {
  aboutTruncated.value = !aboutTruncated.value;
};

</script>
